import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import TryOurTools from '../components/TryOurTools';

const getVideoId = (input: string): string | null => {
  try {
    const url = new URL(input);
    if (url.hostname.endsWith('youtube.com')) {
      return url.searchParams.get('v');
    }
    if (url.hostname === 'youtu.be') {
      return url.pathname.slice(1);
    }
  } catch {
    if (/^[a-zA-Z0-9_-]{11}$/.test(input)) return input;
  }
  return null;
};

const fetchVideoData = async (videoId: string) => {
  // Use oEmbed for public info (no API key needed)
  const url = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Not found');
  return res.json();
};

const TitleDescriptionExtractor: React.FC = () => {
  const [input, setInput] = useState('');
  const [videoId, setVideoId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setTitle('');
    setDescription('');
    setVideoId(null);
    const id = getVideoId(input.trim());
    if (!id) {
      setError('Please enter a valid YouTube URL or video ID.');
      return;
    }
    setLoading(true);
    try {
      const data = await fetchVideoData(id);
      setTitle(data.title);
      setDescription(data.author_name ? `By ${data.author_name}` : '');
      setVideoId(id);
    } catch {
      setError('Could not fetch video info.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>YouTube Title & Description Extractor - Free Tool</title>
        <meta name="description" content="Extract the title and description of any YouTube video instantly. Enter a YouTube URL or video ID to get started." />
        <meta name="keywords" content="youtube title extractor, youtube description extractor, get youtube title, get youtube description" />
        <link rel="canonical" href="/title-description-extractor" />
      </Helmet>
      <div className="max-w-xl mx-auto py-10">
        <h1 className="text-3xl font-bold mb-2 text-light-text dark:text-dark-text text-center">YouTube Title & Description Extractor</h1>
        <p className="text-center text-light-subtle dark:text-dark-subtle mb-8">Get the title and author of any YouTube video. 100% free, no login needed.</p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 mb-6">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Paste YouTube URL or video ID"
            className="flex-1 px-4 py-3 rounded-lg border border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-light-accent dark:focus:ring-dark-accent placeholder-light-subtle dark:placeholder-dark-subtle"
            autoFocus
          />
          <button
            type="submit"
            className="px-6 py-3 rounded-lg bg-light-accent text-white dark:bg-dark-accent dark:text-dark-background font-semibold text-base hover:bg-light-accent_hover dark:hover:bg-dark-accent_hover transition-colors"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Extract'}
          </button>
        </form>
        {error && <div className="text-red-600 dark:text-red-400 mb-4 text-center">{error}</div>}
        {title && (
          <div className="bg-black/5 dark:bg-white/5 rounded-lg p-6 mt-6">
            <div className="text-lg font-bold mb-2 text-light-text dark:text-dark-text">{title}</div>
            {description && <div className="text-light-subtle dark:text-dark-subtle mb-2">{description}</div>}
            {videoId && (
              <a
                href={`https://www.youtube.com/watch?v=${videoId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 px-4 py-2 rounded-lg bg-light-accent text-white dark:bg-dark-accent dark:text-dark-background font-semibold text-sm hover:bg-light-accent_hover dark:hover:bg-dark-accent_hover transition-colors"
              >
                Watch on YouTube
              </a>
            )}
          </div>
        )}
        <div className="mt-8 mb-2 p-4 rounded-lg bg-gradient-to-r from-red-100 to-purple-100 dark:from-red-900/30 dark:to-purple-900/30 border border-light-border dark:border-dark-border text-light-text dark:text-dark-text shadow">
          <strong>YouTube Title & Description Extractor:</strong> Instantly extract the <b>title and description</b> from any YouTube video. Enter a YouTube URL or video ID to get the video title and description for free. No login or API required. Perfect for SEO research, content curation, and social media sharing. 100% free and SEO-friendly.
        </div>
        <TryOurTools />
      </div>
    </>
  );
};

export default TitleDescriptionExtractor; 